export type AuthModalMode = 'signin' | 'signup'

const PENDING_CREATE_STORE_KEY = 'hr-pending-create-store'

/**
 * The auth popup was opened to build a store.
 * Sign-up continues into the create-store form. Sign-in opens My stores.
 */
export function markPendingCreateStore() {
  if (!import.meta.client) return
  sessionStorage.setItem(PENDING_CREATE_STORE_KEY, '1')
}

export function clearPendingCreateStore() {
  if (!import.meta.client) return
  sessionStorage.removeItem(PENDING_CREATE_STORE_KEY)
}

/** Read and clear the create-store handoff. Closing the auth popup also clears it. */
export function takePendingCreateStore(): boolean {
  if (!import.meta.client) return false
  const pending = sessionStorage.getItem(PENDING_CREATE_STORE_KEY) === '1'
  if (pending) sessionStorage.removeItem(PENDING_CREATE_STORE_KEY)
  return pending
}

/**
 * Guest sign-in / sign-up overlay used inside the app shell so visitors can
 * authenticate without leaving the page they were browsing.
 */
export function useAuthModal() {
  const open = useState('auth-modal-open', () => false)
  const mode = useState<AuthModalMode>('auth-modal-mode', () => 'signin')
  /** Session expired: one undismissable popup until sign-in succeeds. */
  const blocking = useState('auth-modal-blocking', () => false)

  function openAuth(next: AuthModalMode = 'signin', options?: { blocking?: boolean }) {
    // Several 401s share the popup that is already on screen.
    if (open.value && blocking.value) return
    if (options?.blocking) {
      blocking.value = true
      mode.value = 'signin'
    }
    else {
      mode.value = next
    }
    open.value = true
  }

  function closeAuth() {
    if (blocking.value) return
    clearPendingCreateStore()
    open.value = false
  }

  /** Closes the popup after a successful sign-in, including the blocking one. */
  function releaseAuthModal() {
    blocking.value = false
    open.value = false
  }

  return { open, mode, blocking, openAuth, closeAuth, releaseAuthModal }
}
